import { useMemo, useState } from "react";
import { format, isSameDay, parseISO, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { getSubtaskProgress, type WorkspaceTask } from "@/hooks/useWorkspaceTasks";
import { TaskPriorityBadge } from "./TaskPriorityBadge";

interface TasksMobileAgendaProps {
  tasks: WorkspaceTask[];
  onView: (task: WorkspaceTask) => void;
}

function dayKey(task: WorkspaceTask): string {
  if (task.due_at) return format(startOfDay(parseISO(task.due_at)), "yyyy-MM-dd");
  if (task.due_date) return task.due_date.slice(0, 10);
  return "none";
}

function timeLabel(task: WorkspaceTask): string | null {
  if (!task.due_at) return null;
  return format(parseISO(task.due_at), "HH:mm");
}

export function TasksMobileAgenda({ tasks, onView }: TasksMobileAgendaProps) {
  const days = useMemo(() => {
    const map = new Map<string, WorkspaceTask[]>();
    for (const task of tasks) {
      const key = dayKey(task);
      const list = map.get(key) ?? [];
      list.push(task);
      map.set(key, list);
    }
    const dated = [...map.keys()].filter((k) => k !== "none").sort();
    if (map.has("none")) dated.push("none");
    return dated.map((key) => ({ key, tasks: map.get(key) ?? [] }));
  }, [tasks]);

  const todayKey = format(new Date(), "yyyy-MM-dd");
  const [selected, setSelected] = useState(() => {
    if (days.some((d) => d.key === todayKey)) return todayKey;
    return days[0]?.key ?? todayKey;
  });

  const selectedDay = days.find((d) => d.key === selected) ?? days[0];
  const selectedTasks = selectedDay?.tasks ?? [];

  if (tasks.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-10">
        Nada agendado neste recorte.
      </p>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div
        className="flex gap-2 overflow-x-auto pb-3 shrink-0 -mx-1 px-1 snap-x snap-mandatory"
        role="tablist"
        aria-label="Dias"
      >
        {days.map((day) => {
          const isNone = day.key === "none";
          const date = isNone ? null : parseISO(`${day.key}T12:00:00`);
          const active = day.key === selected;
          return (
            <button
              key={day.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setSelected(day.key)}
              className={cn(
                "snap-start shrink-0 min-w-[3.25rem] rounded-full px-2.5 py-2 text-center transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active
                  ? "bg-foreground text-background"
                  : "bg-muted/60 text-muted-foreground",
              )}
            >
              <span className="block text-[10px] leading-none opacity-80">
                {isNone ? "—" : format(date!, "EEE", { locale: ptBR })}
              </span>
              <span className="block text-sm font-semibold tabular-nums mt-1">
                {isNone ? "s/ data" : format(date!, "d")}
              </span>
            </button>
          );
        })}
      </div>

      <ul className="flex-1 min-h-0 overflow-y-auto space-y-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
        {selectedTasks.map((task) => {
          const progress = getSubtaskProgress(task);
          const time = timeLabel(task);
          const dueToday = task.due_at
            ? isSameDay(parseISO(task.due_at), new Date())
            : task.due_date === todayKey;
          return (
            <li key={task.id}>
              <button
                type="button"
                onClick={() => onView(task)}
                className="w-full text-left rounded-2xl border bg-card p-4 min-h-[44px] shadow-sm active:bg-muted/40 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-semibold leading-snug line-clamp-2">{task.title}</p>
                  <TaskPriorityBadge priority={task.priority} className="shrink-0" />
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  {time ? (
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {time}
                    </span>
                  ) : dueToday ? (
                    <span>Hoje</span>
                  ) : null}
                  {task.assignee?.display_name ? (
                    <span className="truncate">{task.assignee.display_name}</span>
                  ) : null}
                  {progress.total > 0 ? (
                    <span className="tabular-nums">
                      {progress.done}/{progress.total}
                    </span>
                  ) : null}
                </div>
                {progress.total > 0 ? (
                  <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary transition-[width] duration-300"
                      style={{ width: `${Math.min(100, (progress.done / progress.total) * 100)}%` }}
                    />
                  </div>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
